import { useAsync } from "@/shared/hooks/HookBase";
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  type ReactNode,
  useRef,
} from "react";
import type { UserProfile } from "../../account_manager/models/UserProfile";
import type ILoginService from "../contracts/ILoginService";
import type LoginRequest from "../models/LoginRequest";
import LoginService from "../services/LoginService";
import type IAccountManagerService from "@/features/account_manager/contracts/IAccountManagerService";
import AccountManagerService from "@/features/account_manager/services/AccountManagerService";

interface AuthContextType {
  user: UserProfile | undefined;
  loading: boolean;
  error: Error | null;
  login: (request: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const loginService: ILoginService = new LoginService();
const accountManagerService: IAccountManagerService =
  new AccountManagerService();

export function AuthProvider({ children }: { children: ReactNode }) {
  const { loading, error, execute } = useAsync();
  const [user, setUser] = useState<UserProfile>();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    execute(async () => {
      try {
        const profile = await accountManagerService.getMe();
        setUser(profile);
      } catch {
        setUser(undefined);
      }
    });
  }, [execute]);

  const login = useCallback(
    async (request: LoginRequest) => {
      await execute(async () => {
        const response = await loginService.login(request);
        if (response) {
          const profile = await accountManagerService.getMe();
          setUser(profile);
        }
      });
    },
    [execute],
  );

  const logout = useCallback(() => {
    setUser(undefined);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
